/**
 * PostFeedItem - Single post display with content, media, stats.
 *
 * Layout: avatar, display name, and @username sit on one left-aligned header row;
 * post body and stats (comments) span the card width below—no avatar column beside content.
 * Header row includes an overflow menu (ellipsis icon; delete for own posts, report for all).
 *
 * Clicking the post opens the mobile PostOverlay (if the AppLayout provider
 * is mounted and the viewport is mobile) or falls back to navigating to
 * `/posts/:id` on desktop / standalone contexts. Profile links and the
 * comments button stop propagation so they keep their own behavior.
 *
 * In the feed, the comment button does not expand comments inline — it opens
 * post detail with comments loaded (mobile: `PostOverlay`, desktop:
 * `/posts/:id`). Detail surfaces pass `expandComments` so the composer +
 * comment list render here and the card is no longer a click target for
 * navigation.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { usePostOverlay } from '../../utils/PostOverlayContext';
import PostItemStats from './PostItemStats';
import PostContent from './PostContent';
import PostComments from './PostComments';
import UserAvatar from '../Profile/UserAvatar';
import NavIcon from '../Layout/NavIcon';
import ModalShell from '../../modals/ModalShell';
import ConfirmModal from '../../modals/ConfirmModal';
import { deletePost, reportPost, addResphere, removeResphere } from '../../helpers/postsHelpers';
import { copyTextToClipboard, getPostShareUrl } from '../../helpers/shareLinks';
import { formatRelativeTime } from '../../helpers/timeHelpers';
import useMinuteTick from '../../hooks/useMinuteTick';
import { LoginForCommentsNotice } from '../Share/ShareGatePanels';
import {
  POST_REPORT_REASON_OPTIONS,
  REPORT_OTHER_MIN_DETAILS_LENGTH,
  REPORT_OTHER_REASON_CODE,
} from '../../constants/postReportReasons';

function PostFeedItem({
  post,
  expandComments = false,
  onDelete,
  profileFeedOwner = null,
  feedMode = 'all',
  commentsAccess = 'full',
}) {
  const navigate = useNavigate();
  const { isMobile, openPost } = usePostOverlay();
  const currentUserId = useSelector((s) => s.auth.user?.user_id ?? null);
  const nowMs = useMinuteTick();

  const [menuOpen, setMenuOpen] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportReasonCode, setReportReasonCode] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reportSending, setReportSending] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [resphereBusy, setResphereBusy] = useState(false);
  const [resphereLocal, setResphereLocal] = useState(null);
  const menuContainerRef = useRef(null);

  const poster = {
    user_id: post.poster_id,
    username: post.poster_username,
    displayname: post.poster_displayname,
    profile_pic_url: post.poster_profile_pic_url,
    default_profile_color: post.poster_default_profile_color,
  };

  const isOwnPost =
    currentUserId != null && post.poster_id != null && Number(post.poster_id) === Number(currentUserId);

  const resphereCount = resphereLocal?.resphere_count ?? post.resphere_count ?? 0;
  const userHasResphere = resphereLocal?.user_has_resphered ?? post.user_has_resphered ?? false;

  useEffect(() => {
    setResphereLocal(null);
  }, [post.post_id]);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDocMouseDown = (e) => {
      if (menuContainerRef.current && !menuContainerRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  const openPostDetail = () => {
    if (isMobile) {
      openPost(post.post_id);
      return;
    }
    navigate(`/posts/${post.post_id}`);
  };

  const handleCardClick = () => {
    if (expandComments) return;
    openPostDetail();
  };

  const handleCommentsClick = (e) => {
    e.stopPropagation();
    if (expandComments) return;
    openPostDetail();
  };

  const handleShareClick = async (e) => {
    e.stopPropagation();
    const url = getPostShareUrl(post.post_id);
    if (!url) return;
    const ok = await copyTextToClipboard(url);
    if (ok) toast.success('Link copied to clipboard');
    else toast.error('Could not copy link');
  };

  const handleResphereClick = async (e) => {
    e.stopPropagation();
    if (resphereBusy || currentUserId == null) return;
    setResphereBusy(true);
    try {
      if (userHasResphere) {
        const data = await removeResphere(post.post_id);
        setResphereLocal({
          resphere_count: data.resphere_count,
          user_has_resphered: data.user_has_resphered,
        });
      } else {
        const data = await addResphere(post.post_id);
        setResphereLocal({
          resphere_count: data.resphere_count,
          user_has_resphered: data.user_has_resphered,
        });
      }
    } catch (err) {
      toast.error(err?.message || 'Could not update re-sphere');
    } finally {
      setResphereBusy(false);
    }
  };

  const handleDeleteMenuChoice = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deletePost(post.post_id);
      setDeleteConfirmOpen(false);
      onDelete?.(post.post_id);
      toast.success('Post deleted');
    } catch {
      toast.error('Could not delete post');
    }
  };

  const handleOpenReport = (e) => {
    e.stopPropagation();
    setMenuOpen(false);
    setReportReasonCode('');
    setReportDetails('');
    setReportModalOpen(true);
  };

  const otherDetailsOk =
    reportReasonCode !== REPORT_OTHER_REASON_CODE ||
    reportDetails.trim().length >= REPORT_OTHER_MIN_DETAILS_LENGTH;
  const reportFormValid =
    Boolean(reportReasonCode) && otherDetailsOk;

  const handleSubmitReport = async () => {
    if (!reportFormValid) return;
    setReportSending(true);
    try {
      const body = await reportPost(post.post_id, {
        reason: reportReasonCode,
        details: reportDetails,
      });
      toast.success(
        body.alreadyReported ? 'You already reported this post' : 'Thanks — your report was submitted'
      );
      setReportModalOpen(false);
    } catch (err) {
      toast.error(err?.message || 'Could not submit report');
    } finally {
      setReportSending(false);
    }
  };

  const interactive = !expandComments;
  const otherReasonChosen = reportReasonCode === REPORT_OTHER_REASON_CODE;

  const tsRaw = post.timestamp;
  const timestampTitle =
    tsRaw != null && tsRaw !== ''
      ? Number.isNaN(new Date(tsRaw).getTime())
        ? undefined
        : new Date(tsRaw).toISOString()
      : undefined;
  const relativePostTime = tsRaw != null ? formatRelativeTime(tsRaw, nowMs) : '';
  const showProfileResphereBanner =
    Boolean(profileFeedOwner) && Boolean(post.profile_sent_via_resphere);
  const showFollowingResphereBanner =
    feedMode === 'following' && Boolean(post.following_feed_via_followed_resphere);
  const followingIntroName =
    post.following_resphere_intro_displayname?.trim() ||
    post.following_resphere_intro_username ||
    'Someone';

  return (
    <div
      className={`relative bg-charcoal rounded-2xl p-4 shadow-soft mb-4 transition-colors ${interactive ? 'cursor-pointer hover:bg-charcoal/90' : ''}`}
      data-testid="post-feed-item"
      onClick={interactive ? handleCardClick : undefined}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleCardClick();
              }
            }
          : undefined
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          {showProfileResphereBanner ? (
            <p className="text-silver text-xs" data-testid="profile-resphere-label">
              <span className="text-mist/90">
                {profileFeedOwner.isViewerOwner
                  ? 'You re-sphered this'
                  : `${profileFeedOwner.displayname || profileFeedOwner.username || 'This user'} re-sphered this`}
              </span>
              {post.profile_resphere_at ? (
                <span className="text-silver">
                  {' · '}
                  {formatRelativeTime(post.profile_resphere_at, nowMs)}
                </span>
              ) : null}
            </p>
          ) : null}
          {showFollowingResphereBanner ? (
            <p className="text-silver text-xs" data-testid="following-feed-resphere-label">
              <Link
                to={`/profile/${post.following_resphere_intro_username || ''}`}
                className="font-medium text-mist/90 hover:text-ether"
                onClick={(e) => e.stopPropagation()}
              >
                {followingIntroName}
              </Link>
              <span className="text-mist/90">{' re-sphered this'}</span>
              {post.following_resphere_at ? (
                <span className="text-silver">
                  {' · '}
                  {formatRelativeTime(post.following_resphere_at, nowMs)}
                </span>
              ) : null}
            </p>
          ) : null}
          <div className="flex items-center gap-2 min-w-0" data-testid="post-feed-header">
          <Link
            to={`/profile/${poster.username || ''}`}
            className="flex-shrink-0"
            onClick={(e) => e.stopPropagation()}
          >
            <UserAvatar user={poster} className="w-10 h-10 rounded-full" emojiClassName="text-base" />
          </Link>
          <div className="min-w-0 flex-1">
            <Link
              to={`/profile/${poster.username || ''}`}
              className="block truncate font-medium leading-tight text-ash hover:text-ether"
              onClick={(e) => e.stopPropagation()}
            >
              {poster.displayname || poster.username || 'Unknown'}
            </Link>
            <div className="flex min-w-0 items-center gap-2">
              {poster.username ? (
                <span className="min-w-0 truncate text-sm text-silver">@{poster.username}</span>
              ) : null}
              {relativePostTime ? (
                <time
                  dateTime={timestampTitle}
                  title={timestampTitle}
                  data-testid="post-relative-time"
                  className="shrink-0 text-sm text-silver"
                >
                  {relativePostTime}
                </time>
              ) : null}
            </div>
          </div>
          {currentUserId != null ? (
            <div className="relative shrink-0" ref={menuContainerRef}>
              <button
                type="button"
                aria-haspopup="menu"
                aria-expanded={menuOpen}
                aria-label="Post settings"
                data-testid="post-settings-trigger"
                className="p-2 rounded-lg text-silver hover:text-ash hover:bg-shadow/50 -mr-2 flex items-center justify-center"
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen((o) => !o);
                }}
              >
                <NavIcon icon="ellipsis-horizontal" className="w-5 h-5" />
              </button>
              {menuOpen ? (
                <div
                  className="absolute right-0 top-full mt-1 z-40 w-48 rounded-xl border border-shadow bg-charcoal shadow-soft py-1"
                  role="menu"
                >
                  {isOwnPost ? (
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="post-delete-option"
                      className="w-full text-left px-4 py-2 text-sm text-energy hover:bg-shadow/50"
                      onClick={handleDeleteMenuChoice}
                    >
                      Delete post
                    </button>
                  ) : null}
                  <button
                    type="button"
                    role="menuitem"
                    data-testid="post-report-option"
                    className="w-full text-left px-4 py-2 text-sm text-ash hover:bg-shadow/50"
                    onClick={handleOpenReport}
                  >
                    Report post
                  </button>
                </div>
              ) : null}
            </div>
          ) : (
            <span className="w-10 shrink-0" aria-hidden />
          )}
        </div>
        </div>
        <div className="min-w-0 flex flex-col gap-4">
          <PostContent post={post} stopPropagationOnLinks />
          <PostItemStats
            post={post}
            resphereCount={resphereCount}
            userHasResphered={userHasResphere}
            resphereBusy={resphereBusy}
            onResphereClick={handleResphereClick}
            onCommentsClick={handleCommentsClick}
            onShareClick={handleShareClick}
          />
          {expandComments && (
            <div
              className="border-t border-shadow pt-4"
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              role="presentation"
            >
              {commentsAccess === 'login_required' ? (
                <LoginForCommentsNotice />
              ) : (
                <PostComments postId={post.post_id} />
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={deleteConfirmOpen}
        title="Delete this post?"
        description="This cannot be undone. Comments and aura on this post will be removed."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onCancel={() => setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
      />

      {reportModalOpen ? (
        <ModalShell title="Report post" onClose={() => !reportSending && setReportModalOpen(false)}>
          <p className="text-silver text-sm mb-3">
            Select a category. Options follow App&nbsp;Store Guideline 1.2 expectations for reviewing
            user-generated content (spam, harassment, exploitation, threatening or sexually explicit posts,
            self-harm, child safety concerns, impersonation/IP, illegal activity or regulated goods,
            posts that exceed this app&apos;s age rating where applicable).
          </p>
          <fieldset disabled={reportSending} className="mb-4 rounded-xl border border-shadow p-3">
            <legend className="sr-only">Report category</legend>
            <p className="text-ash text-sm font-medium mb-2 px-1" id="post-report-category-label">
              Why are you reporting this post?
            </p>
            <div
              className="max-h-56 overflow-y-auto space-y-2 pr-1"
              role="radiogroup"
              aria-labelledby="post-report-category-label"
            >
              {POST_REPORT_REASON_OPTIONS.map(({ code, label }) => (
                <label
                  key={code}
                  className="flex cursor-pointer items-start gap-3 rounded-lg p-2 text-left hover:bg-shadow/40"
                >
                  <input
                    type="radio"
                    name="post-report-reason"
                    value={code}
                    className="mt-1 shrink-0 text-ether border-shadow focus:ring-ether"
                    checked={reportReasonCode === code}
                    onChange={() => setReportReasonCode(code)}
                    data-testid={`post-report-reason-${code}`}
                  />
                  <span className="text-sm text-ash leading-snug">{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <label className="block text-silver text-xs font-medium uppercase tracking-wide mb-1">
            {otherReasonChosen
              ? 'Explain briefly (required, at least 10 characters)'
              : 'Additional context (optional)'}
          </label>
          <textarea
            value={reportDetails}
            onChange={(e) => setReportDetails(e.target.value)}
            placeholder={
              otherReasonChosen
                ? `What happened? (${REPORT_OTHER_MIN_DETAILS_LENGTH}+ characters)`
                : 'Optional — add any extra detail for reviewers'
            }
            rows={4}
            disabled={reportSending}
            aria-invalid={
              otherReasonChosen && reportDetails.trim().length > 0 && !otherDetailsOk
                ? 'true'
                : undefined
            }
            className="w-full rounded-xl border border-shadow bg-shadow/30 text-ash placeholder:text-silver px-3 py-2 text-sm resize-y min-h-[96px] focus:outline-none focus:ring-2 focus:ring-ether/50 transition-zen mb-1"
          />
          {otherReasonChosen ? (
            <p className="text-silver text-xs mb-3">
              Under &quot;Something else&quot;, the app collects a brief explanation—required for reviewers
              to act on unspecified issues.
            </p>
          ) : (
            <p className="text-silver text-xs mb-3">
              Selecting a listed reason is usually enough for review; optional notes help when the case
              is subtle.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              disabled={reportSending}
              className="px-4 py-2 rounded-xl border border-shadow text-silver hover:text-ash hover:bg-shadow/50 disabled:opacity-50 transition-colors"
              onClick={() => setReportModalOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={reportSending || !reportFormValid}
              data-testid="post-report-submit"
              className="px-4 py-2 rounded-xl bg-ether text-void font-medium hover:opacity-90 disabled:opacity-50 transition-zen"
              onClick={handleSubmitReport}
            >
              {reportSending ? 'Sending…' : 'Submit report'}
            </button>
          </div>
        </ModalShell>
      ) : null}
    </div>
  );
}

export default PostFeedItem;
